import { captureException } from '@mentimeter/errors/sentry';
import type { Question } from '@mentimeter/legacy-series-types/question';
import memoize from 'lodash/memoize';
import { UnmigratedSlideError } from '../../backward-compatibility/errors/unmigrated-slide-error';
import { getQuestionProperty } from '../../backward-compatibility/question-properties/get-question-property';
import type { CompatibilitySlide } from '../../compatibility-types';
import { InvalidProxySetError } from '../../utils/invalid-proxy-set-error';
import { commonSlidePropertyKeys } from './constants';

type GetCommonSlideCompatibilityHandler = (
  slidePropertyKeys?: Array<string>,
) => ProxyHandler<CompatibilitySlide | Question>;

export const getCommonSlideCompatibilityHandler =
  memoize<GetCommonSlideCompatibilityHandler>(
    (slidePropertyKeys = commonSlidePropertyKeys) => ({
      get(
        target: CompatibilitySlide | Question,
        key:
          | keyof CompatibilitySlide
          | keyof Question
          | '__isProxy'
          | '__target'
          | 'isMigrated',
      ) {
        if (key === '__isProxy') {
          return true;
        }

        if (key === '__target') {
          return target;
        }
        const isTargetMigrated = Reflect.has(target, 'staticContent');

        if (key === 'isMigrated') {
          return isTargetMigrated;
        }

        if (slidePropertyKeys.includes(key)) {
          if (isTargetMigrated) {
            // data is already migrated, return the value directly
            // 'slidePublicKey' -> 'slidePublicKey'
            return target[key as keyof typeof target];
          }

          captureException(new UnmigratedSlideError());

          return (target as any)[key];
        }

        if (isTargetMigrated) {
          // map common slide properties to question properties
          // 'public_key' -> 'slidePublicKey'
          return getQuestionProperty(target as CompatibilitySlide, key as any);
        }

        // data is not migrated yet, return the value directly
        // 'public_key' -> 'public_key'
        return (target as Question)[key as keyof Question];
      },
      set(_: never, key: string) {
        throw new InvalidProxySetError(key, 'slide');
      },
    }),
  );
